import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina, ServiciosPactados, Sucursal } from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, Error, Faltantes, Resultados,
} from '../componentes/comunes';

/**
 * Cuánto se le cobra a Walmart por cada servicio preventivo.
 *
 * Sin esto una orden preventiva no se puede facturar: `tarifa_iguala` da
 * la mensualidad del contrato, no el precio de un servicio.
 *
 * Cada servicio tiene un precio general y, si hace falta, excepciones por
 * sucursal —«en zona metro hay cierto número de sucursales que cuesta
 * diferente»—. La pantalla enseña primero el general, que es lo que
 * aplica a casi todas, y cuántas excepciones tiene, que es lo que dice si
 * ese número cuenta la historia completa.
 *
 * Un precio no se cambia: se captura uno nuevo y el anterior se cierra
 * solo. Las facturas que usaron el viejo siguen existiendo.
 */

interface ServicioPreventivo {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  area: Opcion | null;
  /** Nulo mientras nadie le haya capturado un precio general vigente hoy. */
  precioGeneral: number | null;
  /** Uno capturado que todavía no entra en vigor. Sólo si no hay vigente. */
  precioProximo: number | null;
  precioProximoDesde: string | null;
  excepciones: number;
}

interface PrecioDeServicio {
  id: string;
  precio: number;
  vigenteDesde: string;
  vigenteHasta: string | null;
  nota: string | null;
  /** Nulo es el precio general; con sucursal, la excepción de esa tienda. */
  sucursal: {
    id: string; determinante: string; nombre: string; region: Opcion | null;
  } | null;
  vigente: boolean;
}

interface PreciosDelServicio {
  servicio: { id: string; codigo: string; nombre: string };
  precios: PrecioDeServicio[];
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/**
 * Hoy en formato de campo de fecha, que es lo que espera el input.
 *
 * Armado con las partes locales y no con `toISOString`, que da la fecha
 * en UTC: capturando a partir de las seis de la tarde en México, ese
 * atajo devuelve **mañana**, y el precio nacía con un día de retraso sin
 * que nadie lo notara hasta ver la columna «Desde».
 */
function hoy(): string {
  const ahora = new Date();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');

  return `${ahora.getFullYear()}-${mes}-${dia}`;
}

/**
 * Los precios y lo pactado, en dos vistas.
 *
 * Cuánto cuesta cada servicio es una pregunta; qué servicios lleva cada
 * sucursal es otra, y se contestan en momentos distintos. Las dos hacen
 * falta para que una preventiva se pueda facturar: sin precio no hay
 * importe, y sin lista pactada no hay qué multiplicar.
 */
export default function PreciosPreventivos() {
  const [vista, setVista] = useState<'precios' | 'pactado'>('precios');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Precios del preventivo</h1>
          <p className="sub">
            Lo que se le cobra al cliente por cada servicio de
            mantenimiento preventivo, y qué servicios lleva cada sucursal.
          </p>
        </div>
      </div>

      <nav className="submenu">
        <button
          className={vista === 'precios' ? 'activo' : ''}
          onClick={() => setVista('precios')}
        >
          Servicios y precios
        </button>
        <button
          className={vista === 'pactado' ? 'activo' : ''}
          onClick={() => setVista('pactado')}
        >
          Qué lleva cada sucursal
        </button>
      </nav>

      {vista === 'precios' ? <Servicios /> : <Pactado />}
    </>
  );
}

function Servicios() {
  const { puede } = useSesion();
  const [servicios, setServicios] = useState<ServicioPreventivo[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [creando, setCreando] = useState(false);
  const [abierto, setAbierto] = useState<ServicioPreventivo | null>(null);
  const [editando, setEditando] = useState<ServicioPreventivo | null>(null);
  const [borrando, setBorrando] = useState<ServicioPreventivo | null>(null);
  /** Abrir el servicio ya con el formulario de precio puesto. */
  const [capturarYa, setCapturarYa] = useState(false);

  const captura = puede('catalogos', 'crear');
  const administra = puede('catalogos', 'editar');
  const puedeEliminar = puede('catalogos', 'eliminar');

  const cargar = useCallback(() => {
    api.get<ServicioPreventivo[]>('/servicios-preventivos', {
      busqueda: busqueda || undefined,
    })
      .then(setServicios)
      .catch(setError);
  }, [busqueda]);

  /**
   * Desactivar es inmediato y no pide confirmación: se deshace con el
   * mismo botón, que ahora dice «Reactivar». Eliminar sí la pide, porque
   * de eso no se vuelve.
   */
  async function cambiarEstado(servicio: ServicioPreventivo, activo: boolean) {
    setError(null);
    try {
      await api.patch(`/servicios-preventivos/${servicio.id}`, { activo });
      cargar();
    } catch (e) {
      setError(e);
    }
  }

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <>
      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Código o nombre del servicio…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {captura && (
            <button className="primario" onClick={() => setCreando(true)}>
              Nuevo servicio
            </button>
          )}
        </div>

        <Error error={error} />

        {!servicios ? <Cargando /> : servicios.length === 0 ? (
          <div className="vacio">
            {busqueda
              ? 'Ningún servicio con esa búsqueda.'
              : 'Todavía no hay servicios preventivos capturados. Los define'
                + ' APRISA: sin ellos, una orden preventiva no tiene de dónde'
                + ' sacar su precio.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Servicio</th>
                <th>Área</th>
                <th className="numero">Precio general</th>
                <th>Excepciones</th>
                {administra && <th />}
              </tr>
            </thead>
            <tbody>
              {servicios.map((s) => (
                <tr key={s.id} style={{ opacity: s.activo ? 1 : 0.55 }}>
                  <td className="mono">
                    <button className="enlace" onClick={() => setAbierto(s)}>{s.codigo}</button>
                  </td>
                  <td>
                    {s.nombre}
                    {!s.activo && <span className="etiqueta" style={{ marginLeft: 8 }}>inactivo</span>}
                  </td>
                  <td>{s.area?.nombre ?? '—'}</td>
                  <td className="numero">
                    {s.precioGeneral !== null ? moneda(s.precioGeneral)
                      : s.precioProximo !== null ? (
                        /* Capturado y todavía sin entrar. Decir «sin
                           capturar» de esto es lo que hace que alguien lo
                           capture otra vez. */
                        <>
                          {moneda(s.precioProximo)}
                          <span className="ayuda">
                            {' '}entra el {fechaCorta(s.precioProximoDesde!)}
                          </span>
                        </>
                      ) : (
                      /* Sin precio general no es «cero»: es que nadie lo
                         ha pactado, y una preventiva de ese servicio no
                         se puede facturar hasta que alguien lo capture.
                         El botón está aquí porque aquí se nota el hueco;
                         escondido detrás del código del servicio, dar de
                         alta uno y no encontrar dónde ponerle precio era
                         lo más fácil que podía pasar. */
                      captura
                        ? (
                          <button
                            className="enlace"
                            onClick={() => { setAbierto(s); setCapturarYa(true); }}
                          >
                            Capturar precio
                          </button>
                        )
                        : <span className="etiqueta no">sin capturar</span>
                    )}
                  </td>
                  <td>
                    {s.excepciones > 0
                      ? `${s.excepciones} sucursal(es) cobran distinto`
                      : '—'}
                  </td>
                  {administra && (
                    <td>
                      <div className="controles">
                        <button className="enlace" onClick={() => setEditando(s)}>
                          Editar
                        </button>
                        {/* Desactivar y eliminar no son lo mismo y por eso
                            están los dos: lo que ya se usó se desactiva
                            —deja de ofrecerse y las facturas viejas
                            siguen comprobándose— y lo que nunca se usó,
                            una errata de captura, se quita. */}
                        <button
                          className="enlace"
                          onClick={() => void cambiarEstado(s, !s.activo)}
                        >
                          {s.activo ? 'Desactivar' : 'Reactivar'}
                        </button>
                        {puedeEliminar && (
                          <button className="enlace" onClick={() => setBorrando(s)}>
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creando && (
        <FormularioServicio
          onCerrar={() => setCreando(false)}
          onGuardar={() => { setCreando(false); cargar(); }}
        />
      )}

      {editando && (
        <FormularioServicio
          servicio={editando}
          onCerrar={() => setEditando(null)}
          onGuardar={() => { setEditando(null); cargar(); }}
        />
      )}

      {borrando && (
        <ConfirmarBorrado
          servicio={borrando}
          onCerrar={() => setBorrando(null)}
          onHecho={() => { setBorrando(null); cargar(); }}
        />
      )}

      {abierto && (
        <PreciosDelServicioDialogo
          servicio={abierto}
          captura={captura}
          capturarAlAbrir={capturarYa}
          onCerrar={() => { setAbierto(null); setCapturarYa(false); }}
          onCambio={cargar}
        />
      )}
    </>
  );
}

/**
 * Quitar un servicio del catálogo.
 *
 * Se pregunta primero qué depende de él y se enseña antes de borrar. La
 * API lo impide igual si hay algo, pero enterarse al apretar el botón es
 * peor que saberlo antes: quien lo va a quitar quiere decidir con el dato
 * a la vista, no que le digan que no.
 */
function ConfirmarBorrado({ servicio, onCerrar, onHecho }: {
  servicio: ServicioPreventivo;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [uso, setUso] = useState<Record<string, number> | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Record<string, number>>(`/servicios-preventivos/${servicio.id}/uso`)
      .then(setUso)
      .catch(setError);
  }, [servicio.id]);

  const seUso = uso !== null && Object.keys(uso).length > 0;

  async function borrar() {
    setBorrando(true);
    setError(null);
    try {
      await api.borrar(`/servicios-preventivos/${servicio.id}`);
      onHecho();
    } catch (e) {
      setError(e);
      setBorrando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Eliminar ${servicio.codigo}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={borrando}>Cancelar</button>
          {!seUso && (
            <button
              className="primario"
              onClick={borrar}
              disabled={uso === null || borrando}
            >
              {borrando ? 'Eliminando…' : 'Eliminar'}
            </button>
          )}
        </>
      )}
    >
      <Error error={error} />

      {uso === null ? <Cargando /> : seUso ? (
        <>
          <div className="aviso">
            Este servicio ya se usó, así que no se puede eliminar:
            {' '}
            {Object.entries(uso).map(([que, n]) => `${n} ${que}`).join(', ')}.
          </div>
          <p className="ayuda">
            Desactívalo desde la lista: deja de ofrecerse al capturar y al
            cerrar visitas, y lo que ya se facturó con él sigue pudiendo
            comprobarse. Borrarlo dejaría facturas apuntando a algo que no
            existe.
          </p>
        </>
      ) : (
        <p>
          Nunca se ha usado: no tiene precios capturados, ninguna sucursal
          lo lleva y no aparece en ninguna orden. Se puede quitar sin
          consecuencias.
        </p>
      )}
    </Dialogo>
  );
}

/**
 * Qué servicios preventivos lleva cada sucursal.
 *
 * Son 365 sucursales: se buscan una por una para corregir, y se capturan
 * en bloque por zona, formato o iguala para poder empezar. En bloque sólo
 * agrega, nunca quita: un «reemplazar» sobre el filtro equivocado borra
 * listas que costó capturar y no hay cómo saber cuáles eran.
 */
function Pactado() {
  const { puede } = useSesion();
  const [resumen, setResumen] = useState<{
    total: number; conServicios: number; sinServicios: number;
  } | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [abierta, setAbierta] = useState<Sucursal | null>(null);
  const [enBloque, setEnBloque] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const captura = puede('catalogos', 'editar');

  const cargarResumen = useCallback(() => {
    api.get<{ total: number; conServicios: number; sinServicios: number }>(
      '/servicios-preventivos/pactados/resumen',
    )
      .then(setResumen)
      .catch(() => undefined);
  }, []);

  useEffect(cargarResumen, [cargarResumen]);

  useEffect(() => {
    if (busqueda.trim().length < 2) { setSucursales([]); return undefined; }
    const t = setTimeout(() => {
      api.get<Pagina<Sucursal>>('/sucursales', {
        busqueda: busqueda.trim(), estado: 'activo', limite: 20,
      })
        .then((r) => setSucursales(r.data))
        .catch(setError);
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  return (
    <>
      {resumen && (
        <div className="rejilla-catalogos">
          <div className="tarjeta" style={{ cursor: 'default' }}>
            <div className="conteo">Sucursales con servicios pactados</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumen.conServicios}</div>
          </div>
          <div className="tarjeta" style={{ cursor: 'default' }}>
            <div className="conteo">Sin ninguno</div>
            {/* Cada una de éstas es una preventiva que no se va a poder
                facturar, y no se nota hasta que llega el mes. */}
            <div style={{
              fontSize: 22,
              fontWeight: 700,
              color: resumen.sinServicios > 0 ? '#b02a37' : undefined,
            }}>
              {resumen.sinServicios}
            </div>
          </div>
          <div className="tarjeta" style={{ cursor: 'default' }}>
            <div className="conteo">Sucursales activas</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumen.total}</div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Determinante o nombre de la sucursal…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {captura && (
            <button className="primario" onClick={() => setEnBloque(true)}>
              Capturar en bloque
            </button>
          )}
        </div>

        <Error error={error} />

        {sucursales.length === 0 ? (
          <div className="vacio">
            Busca una sucursal por determinante o por nombre para ver y
            corregir su lista. Para llenarlas de golpe, «Capturar en
            bloque» aplica los mismos servicios a toda una zona, formato o
            tipo de iguala.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Determinante</th>
                <th>Sucursal</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {sucursales.map((s) => (
                <tr key={s.id}>
                  <td className="mono">
                    <button className="enlace" onClick={() => setAbierta(s)}>
                      {s.determinante}
                    </button>
                  </td>
                  <td>{s.nombre}</td>
                  <td>{s.estado?.nombre ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {abierta && (
        <ListaDeLaSucursal
          sucursalId={abierta.id}
          captura={captura}
          onCerrar={() => setAbierta(null)}
          onGuardar={cargarResumen}
        />
      )}

      {enBloque && (
        <CapturaEnBloque
          onCerrar={() => setEnBloque(false)}
          onGuardar={() => { setEnBloque(false); cargarResumen(); }}
        />
      )}
    </>
  );
}

function ListaDeLaSucursal({ sucursalId, captura, onCerrar, onGuardar }: {
  sucursalId: string;
  captura: boolean;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [datos, setDatos] = useState<ServiciosPactados | null>(null);
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<ServiciosPactados>('/servicios-preventivos/pactados', { sucursalId })
      .then((r) => {
        setDatos(r);
        setElegidos(new Set(r.servicios.filter((s) => s.pactado).map((s) => s.id)));
      })
      .catch(setError);
  }, [sucursalId]);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.post('/servicios-preventivos/pactados', {
        sucursalId,
        servicios: [...elegidos],
      });
      onGuardar();
      onCerrar();
    } catch (e) {
      setError(e);
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo={datos
        ? `${datos.sucursal.determinante} — ${datos.sucursal.nombre}`
        : 'Servicios pactados'}
      onCerrar={onCerrar}
      pie={captura && (
        <button className="primario" onClick={guardar} disabled={guardando || !datos}>
          {guardando ? 'Guardando…' : 'Guardar la lista'}
        </button>
      )}
    >
      <Error error={error} />

      {!datos ? <Cargando /> : datos.servicios.length === 0 ? (
        <div className="vacio">
          No hay servicios preventivos en el catálogo todavía.
        </div>
      ) : (
        <>
          <p className="ayuda">
            Lo que quede palomeado es lo que esta sucursal lleva
            contratado, y lo que se le va a proponer al cerrar cada visita.
          </p>
          {datos.servicios.map((s) => (
            <label key={s.id} style={{ display: 'block', fontWeight: 400 }}>
              <input
                type="checkbox"
                disabled={!captura}
                checked={elegidos.has(s.id)}
                onChange={() => setElegidos((antes) => {
                  const ahora = new Set(antes);
                  if (ahora.has(s.id)) ahora.delete(s.id);
                  else ahora.add(s.id);
                  return ahora;
                })}
              />{' '}
              <span className="mono">{s.codigo}</span> · {s.nombre}
            </label>
          ))}
        </>
      )}
    </Dialogo>
  );
}

function CapturaEnBloque({ onCerrar, onGuardar }: {
  onCerrar: () => void; onGuardar: () => void;
}) {
  const [catalogo, setCatalogo] = useState<ServicioPreventivo[]>([]);
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [formatos, setFormatos] = useState<Opcion[]>([]);
  const [igualas, setIgualas] = useState<Opcion[]>([]);
  const [regionId, setRegionId] = useState('');
  const [formatoTiendaId, setFormatoTiendaId] = useState('');
  const [tipoIgualaId, setTipoIgualaId] = useState('');
  const [hecho, setHecho] = useState<{ sucursales: number; servicios: number } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<ServicioPreventivo[]>('/servicios-preventivos', { estado: 'activo' })
      .then(setCatalogo).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/formatos-tienda/opciones')
      .then(setFormatos).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/tipos-iguala/opciones').then(setIgualas).catch(() => undefined);
  }, []);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const r = await api.post<{ sucursales: number; servicios: number }>(
        '/servicios-preventivos/pactados/en-bloque',
        {
          servicios: [...elegidos],
          regionId: regionId || undefined,
          formatoTiendaId: formatoTiendaId || undefined,
          tipoIgualaId: tipoIgualaId || undefined,
        },
      );
      setHecho(r);
      onGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo="Capturar servicios en bloque"
      ancho
      onCerrar={onCerrar}
      pie={!hecho && (
        <button
          className="primario"
          onClick={guardar}
          disabled={elegidos.size === 0 || guardando}
        >
          {guardando ? 'Aplicando…' : 'Agregar a las sucursales del filtro'}
        </button>
      )}
    >
      <Error error={error} />

      {hecho ? (
        <div className="vacio">
          Listo: {hecho.servicios} servicio(s) agregados a {hecho.sucursales} sucursal(es).
        </div>
      ) : (
        <>
          <p className="ayuda">
            Esto <strong>agrega</strong>, no reemplaza: una sucursal que ya
            llevaba otros servicios los conserva. Quitar se hace sucursal
            por sucursal, a propósito.
          </p>

          <div className="rejilla-campos">
            <div className="campo">
              <label htmlFor="zona">Zona</label>
              <select id="zona" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
                <option value="">Todas</option>
                {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="formato">Formato de tienda</label>
              <select
                id="formato"
                value={formatoTiendaId}
                onChange={(e) => setFormatoTiendaId(e.target.value)}
              >
                <option value="">Todos</option>
                {formatos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="iguala">Tipo de iguala</label>
              <select
                id="iguala"
                value={tipoIgualaId}
                onChange={(e) => setTipoIgualaId(e.target.value)}
              >
                <option value="">Todos</option>
                {igualas.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
              </select>
            </div>
          </div>

          <div className="campo">
            <label>Servicios que se agregan</label>
            {catalogo.length === 0 ? (
              <span className="ayuda">No hay servicios preventivos capturados todavía.</span>
            ) : catalogo.map((s) => (
              <label key={s.id} style={{ display: 'block', fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={elegidos.has(s.id)}
                  onChange={() => setElegidos((antes) => {
                    const ahora = new Set(antes);
                    if (ahora.has(s.id)) ahora.delete(s.id);
                    else ahora.add(s.id);
                    return ahora;
                  })}
                />{' '}
                <span className="mono">{s.codigo}</span> · {s.nombre}
              </label>
            ))}
          </div>
        </>
      )}
    </Dialogo>
  );
}

/**
 * Alta y corrección de un servicio.
 *
 * El mismo formulario para las dos cosas: cambian el título y si el
 * código se puede tocar. El código no se corrige —es la referencia con la
 * que se capturaron los precios— y por eso al editar sale bloqueado en
 * vez de desaparecer: hace falta verlo para saber cuál se está tocando.
 */
function FormularioServicio({ servicio, onCerrar, onGuardar }: {
  servicio?: ServicioPreventivo;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [codigo, setCodigo] = useState(servicio?.codigo ?? '');
  const [nombre, setNombre] = useState(servicio?.nombre ?? '');
  const [areaId, setAreaId] = useState(servicio?.area?.id ?? '');
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
  }, []);

  const listo = codigo.trim().length >= 2 && nombre.trim().length >= 3;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      if (servicio) {
        await api.patch(`/servicios-preventivos/${servicio.id}`, {
          nombre: nombre.trim(),
          areaId: areaId || undefined,
        });
      } else {
        await api.post('/servicios-preventivos', {
          codigo: codigo.trim(),
          nombre: nombre.trim(),
          areaId: areaId || undefined,
        });
      }
      onGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo={servicio ? `Editar ${servicio.codigo}` : 'Nuevo servicio preventivo'}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="codigo">Código</label>
        <input
          id="codigo"
          value={codigo}
          disabled={Boolean(servicio)}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="PREV-CAMARA"
        />
        <span className="ayuda">
          {servicio
            ? 'El código no se cambia: con él se capturaron los precios.'
            : 'Se guarda en mayúsculas y no se puede repetir.'}
        </span>
      </div>

      <div className="campo">
        <label htmlFor="nombre">Nombre</label>
        <input
          id="nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Mantenimiento preventivo a cámara de refrigeración"
        />
      </div>

      <div className="campo">
        <label htmlFor="area">Área</label>
        <select id="area" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">Sin clasificar</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <span className="ayuda">
          Se puede dejar para después: el precio es lo que hace falta para
          poder facturar.
        </span>
      </div>
    </Dialogo>
  );
}

/**
 * El precio general del servicio y las sucursales que cobran distinto.
 *
 * Se enseñan también los precios cerrados, en gris. Al revisar un importe
 * ya facturado lo que se quiere ver es la historia: esconder lo que dejó
 * de aplicar obliga a creerle a la pantalla en vez de poder comprobarlo.
 */
function PreciosDelServicioDialogo({
  servicio, captura, capturarAlAbrir, onCerrar, onCambio,
}: {
  servicio: ServicioPreventivo;
  captura: boolean;
  /** Se abrió desde «Capturar precio»: el formulario va puesto de una vez. */
  capturarAlAbrir?: boolean;
  onCerrar: () => void;
  onCambio: () => void;
}) {
  const [datos, setDatos] = useState<PreciosDelServicio | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [capturando, setCapturando] = useState(Boolean(capturarAlAbrir));
  const [corrigiendo, setCorrigiendo] = useState<PrecioDeServicio | null>(null);
  const [quitando, setQuitando] = useState<PrecioDeServicio | null>(null);

  const cargar = useCallback(() => {
    api.get<PreciosDelServicio>(`/servicios-preventivos/${servicio.id}/precios`)
      .then(setDatos)
      .catch(setError);
  }, [servicio.id]);

  useEffect(cargar, [cargar]);

  const generales = datos?.precios.filter((p) => !p.sucursal) ?? [];
  const excepciones = datos?.precios.filter((p) => p.sucursal) ?? [];

  return (
    <Dialogo
      titulo={`${servicio.codigo} · ${servicio.nombre}`}
      ancho
      onCerrar={onCerrar}
      pie={captura && (
        <button className="primario" onClick={() => setCapturando(true)}>
          Capturar precio
        </button>
      )}
    >
      <Error error={error} />

      {!datos ? <Cargando /> : (
        <>
          <h3>Precio general</h3>
          {generales.length === 0 ? (
            <div className="vacio">
              Sin precio general. Mientras no lo tenga, una preventiva de
              este servicio no se puede facturar en ninguna sucursal.
            </div>
          ) : (
            <TablaDePrecios
              precios={generales}
              administra={captura}
              onEditar={setCorrigiendo}
              onEliminar={setQuitando}
            />
          )}

          <h3 style={{ marginTop: 20 }}>Sucursales que cobran distinto</h3>
          {excepciones.length === 0 ? (
            <div className="vacio">
              Ninguna. Todas las sucursales van al precio general.
            </div>
          ) : (
            <TablaDePrecios
              precios={excepciones}
              conSucursal
              administra={captura}
              onEditar={setCorrigiendo}
              onEliminar={setQuitando}
            />
          )}
        </>
      )}

      {capturando && (
        <FormularioPrecio
          servicio={servicio}
          onCerrar={() => setCapturando(false)}
          onGuardar={() => { setCapturando(false); cargar(); onCambio(); }}
        />
      )}

      {corrigiendo && (
        <CorregirPrecio
          precio={corrigiendo}
          onCerrar={() => setCorrigiendo(null)}
          onGuardar={() => { setCorrigiendo(null); cargar(); onCambio(); }}
        />
      )}

      {quitando && (
        <QuitarPrecio
          precio={quitando}
          onCerrar={() => setQuitando(null)}
          onHecho={() => { setQuitando(null); cargar(); onCambio(); }}
        />
      )}
    </Dialogo>
  );
}

/**
 * Corregir un renglón de precio.
 *
 * Es para la errata al teclear: 1,200 donde iban 12,000. Subir el precio
 * no se hace aquí —para eso se captura uno nuevo con su fecha y el
 * anterior se cierra solo—, porque cambiarle el importe a un renglón
 * vigente borra que alguna vez se cobró el viejo.
 *
 * Si con ese renglón ya se facturó, se avisa antes: lo facturado no
 * cambia, así que las dos cifras van a dejar de coincidir.
 */
function CorregirPrecio({ precio, onCerrar, onGuardar }: {
  precio: PrecioDeServicio;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [importe, setImporte] = useState(String(precio.precio));
  const [desde, setDesde] = useState(precio.vigenteDesde.slice(0, 10));
  const [hasta, setHasta] = useState(precio.vigenteHasta?.slice(0, 10) ?? '');
  const [nota, setNota] = useState(precio.nota ?? '');
  const [uso, setUso] = useState<{ ordenes: number } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<{ ordenes: number }>(`/precios-preventivos/${precio.id}/uso`)
      .then(setUso)
      .catch(() => undefined);
  }, [precio.id]);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.patch(`/precios-preventivos/${precio.id}`, {
        precio: Number(importe),
        vigenteDesde: desde,
        // Vacío es «sigue vigente»: nulo explícito, porque no mandarlo
        // dejaría la fecha vieja puesta.
        vigenteHasta: hasta || null,
        nota: nota.trim(),
      });
      onGuardar();
    } catch (e) {
      setError(e);
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo="Corregir el precio"
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={Number(importe) <= 0 || !desde || guardando}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      {uso && uso.ordenes > 0 && (
        <div className="aviso">
          Con este precio ya se facturaron {uso.ordenes} orden(es). Lo
          facturado no cambia —el importe quedó copiado en cada orden—, así
          que a partir de aquí el catálogo va a decir una cifra y esas
          facturas otra.
        </div>
      )}

      <p className="ayuda">
        Esto es para corregir una errata. Si lo que pasó es que el precio
        subió, cierra este diálogo y captura uno nuevo con la fecha en que
        entra: éste se cierra solo y se conserva lo que se cobró antes.
      </p>

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="importe">Precio</label>
          <input
            id="importe"
            type="number"
            min="0"
            step="0.01"
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="c-desde">Vigente desde</label>
          <input
            id="c-desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="c-hasta">Vigente hasta</label>
          <input
            id="c-hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <span className="ayuda">En blanco, sigue vigente.</span>
        </div>
      </div>

      <div className="campo">
        <label htmlFor="c-nota">Nota</label>
        <input id="c-nota" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
    </Dialogo>
  );
}

/** Quitar un renglón de precio que nunca se usó. */
function QuitarPrecio({ precio, onCerrar, onHecho }: {
  precio: PrecioDeServicio;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [uso, setUso] = useState<{ ordenes: number } | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<{ ordenes: number }>(`/precios-preventivos/${precio.id}/uso`)
      .then(setUso)
      .catch(setError);
  }, [precio.id]);

  const seUso = uso !== null && uso.ordenes > 0;

  async function borrar() {
    setBorrando(true);
    setError(null);
    try {
      await api.borrar(`/precios-preventivos/${precio.id}`);
      onHecho();
    } catch (e) {
      setError(e);
      setBorrando(false);
    }
  }

  return (
    <Dialogo
      titulo="Eliminar el precio"
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={borrando}>Cancelar</button>
          {!seUso && (
            <button
              className="primario"
              onClick={borrar}
              disabled={uso === null || borrando}
            >
              {borrando ? 'Eliminando…' : 'Eliminar'}
            </button>
          )}
        </>
      )}
    >
      <Error error={error} />

      {uso === null ? <Cargando /> : seUso ? (
        <>
          <div className="aviso">
            Ya se facturaron {uso.ordenes} orden(es) con este precio, así
            que no se puede eliminar.
          </div>
          <p className="ayuda">
            Para que deje de aplicarse, ponle fecha de cierre con
            «Corregir». Así se conserva lo que se cobró mientras estuvo
            vigente.
          </p>
        </>
      ) : (
        <p>
          Nunca se facturó con este precio, así que se puede quitar. Si al
          capturarlo se cerró el precio anterior, ése vuelve a quedar
          vigente.
        </p>
      )}
    </Dialogo>
  );
}

function TablaDePrecios({ precios, conSucursal, administra, onEditar, onEliminar }: {
  precios: PrecioDeServicio[];
  conSucursal?: boolean;
  administra?: boolean;
  onEditar?: (p: PrecioDeServicio) => void;
  onEliminar?: (p: PrecioDeServicio) => void;
}) {
  return (
    <table>
      <thead>
        <tr>
          {conSucursal && <th>Sucursal</th>}
          <th className="numero">Precio</th>
          <th>Desde</th>
          <th>Hasta</th>
          <th>Nota</th>
          {administra && <th />}
        </tr>
      </thead>
      <tbody>
        {precios.map((p) => (
          <tr key={p.id} style={{ opacity: p.vigente ? 1 : 0.55 }}>
            {conSucursal && (
              <td>
                {p.sucursal?.determinante} — {p.sucursal?.nombre}
                {p.sucursal?.region && (
                  <span className="ayuda"> · {p.sucursal.region.nombre}</span>
                )}
              </td>
            )}
            <td className="numero">{moneda(p.precio)}</td>
            <td>{fechaCorta(p.vigenteDesde)}</td>
            <td>
              {/* «Vigente» sólo si de verdad lo está. Decía que sí a todo
                  lo que no tuviera fecha de cierre, incluidos los precios
                  que empiezan la semana que entra: la tabla los daba por
                  buenos y la columna del listado los ignoraba, con razón,
                  y las dos parecían contradecirse. */}
              {p.vigenteHasta
                ? fechaCorta(p.vigenteHasta)
                : p.vigente
                  ? <span className="etiqueta si">vigente</span>
                  : <span className="etiqueta">aún no empieza</span>}
            </td>
            <td>{p.nota ?? '—'}</td>
            {administra && (
              <td>
                <div className="controles">
                  <button className="enlace" onClick={() => onEditar?.(p)}>Corregir</button>
                  <button className="enlace" onClick={() => onEliminar?.(p)}>Eliminar</button>
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FormularioPrecio({ servicio, onCerrar, onGuardar }: {
  servicio: ServicioPreventivo;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [esExcepcion, setEsExcepcion] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucursalId, setSucursalId] = useState('');
  const [precio, setPrecio] = useState('');
  const [vigenteDesde, setVigenteDesde] = useState(hoy());
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Son 365 sucursales: se buscan, no se listan.
  useEffect(() => {
    if (!esExcepcion || busqueda.trim().length < 2) { setSucursales([]); return undefined; }
    const t = setTimeout(() => {
      api.get<Pagina<Sucursal>>('/sucursales', {
        busqueda: busqueda.trim(), estado: 'activo', limite: 20,
      })
        .then((r) => setSucursales(r.data))
        .catch(() => setSucursales([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda, esExcepcion]);

  const elegida = sucursales.find((s) => s.id === sucursalId);

  /**
   * Qué falta, dicho antes de apretar.
   *
   * Un botón apagado sin explicación se lee como «no me deja», y quien lo
   * mira culpa al último campo que tocó.
   */
  const faltantes: string[] = [];
  if (esExcepcion && !sucursalId) faltantes.push('elegir la sucursal');
  if (!(Number(precio) > 0)) faltantes.push('capturar el precio');
  if (!vigenteDesde) faltantes.push('decir desde cuándo aplica');

  const listo = faltantes.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.post('/precios-preventivos', {
        servicioPreventivoId: servicio.id,
        sucursalId: esExcepcion ? sucursalId : undefined,
        precio: Number(precio),
        vigenteDesde,
        nota: nota.trim() || undefined,
      });
      onGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Capturar precio · ${servicio.codigo}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        El precio que estuviera abierto para esta misma combinación se
        cierra la víspera. No se pierde: las facturas que lo usaron siguen
        pudiendo comprobarse.
      </p>

      <div className="campo">
        <label htmlFor="alcance">Aplica a</label>
        <select
          id="alcance"
          value={esExcepcion ? 'sucursal' : 'general'}
          onChange={(e) => {
            setEsExcepcion(e.target.value === 'sucursal');
            setSucursalId('');
          }}
        >
          <option value="general">Todas las sucursales</option>
          <option value="sucursal">Sólo una sucursal</option>
        </select>
      </div>

      {esExcepcion && (
        <div className="campo">
          <label htmlFor="sucursal">Sucursal</label>

          {/* Elegida, se enseña y la lista se retira: dejarla abierta con
              un renglón resaltado no dice si el clic tomó, porque el
              resaltado también aparece al mover el teclado. */}
          {elegida ? (
            <div className="controles">
              <strong>{elegida.determinante} — {elegida.nombre}</strong>
              <button className="enlace" onClick={() => setSucursalId('')}>
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <input
                id="sucursal"
                type="search"
                value={busqueda}
                placeholder="Determinante o nombre…"
                onChange={(e) => { setBusqueda(e.target.value); setSucursalId(''); }}
              />
              {sucursales.length > 0 && (
                <Resultados
                  de={sucursales}
                  clave={(s) => s.id}
                  como={(s) => `${s.determinante} — ${s.nombre}`}
                  onElegir={(s) => setSucursalId(s.id)}
                />
              )}
              {busqueda.trim().length >= 2 && sucursales.length === 0 && (
                <span className="ayuda">Ninguna sucursal con esa búsqueda.</span>
              )}
            </>
          )}
        </div>
      )}

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="precio">Precio</label>
          <input
            id="precio"
            type="number"
            min="0"
            step="0.01"
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor="desde">Vigente desde</label>
          <input
            id="desde"
            type="date"
            value={vigenteDesde}
            onChange={(e) => setVigenteDesde(e.target.value)}
          />
        </div>
      </div>

      <div className="campo">
        <label htmlFor="nota">Nota</label>
        <input
          id="nota"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="De qué acuerdo salió este precio"
        />
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}
